import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CurrencyInputComponent } from '../../../../shared/currency-input/currency-input.component';
import { AbstractControl, ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { SharedModule } from '../../../../shared/shared.module';
import { CheckNumberInputComponent } from '../../../../shared/check-number-input/check-number-input.component';
import { BankAccountsService } from '../../../../../services/financial-accounts/bank-accounts.service';
import { CheckFormatOptionsComponent } from '../check-format-options/check-format-options.component';
import { VendorService } from '../../../../../services/billing/vendor.service';
import { JwtLegFiClaims } from '../../../../../services/auth/jwt-legfi-claims.model';
import { LegFiJwtService } from '../../../../../services/auth/legfi-jwt.service';
import { Vendor } from 'app/models/entities/vendors/vendor';
import { VendorEditorDialogComponent, VendorEditorDialogResult } from '../../../bill-pay/vendor-editor-dialog/vendor-editor-dialog.component';
import { animate, style, transition, trigger } from '@angular/animations';
import { CheckPrintingService } from '../../../../../services/payables/check-printing.service';
import { ConfirmDialogComponent } from '../../../../v2-layout/shared/dialogs/confirm-dialog/confirm-dialog.component';
import { catchError, finalize, switchMap, take } from 'rxjs/operators';
import { GrowlerService } from '../../../../shared/growler/growler.service';
import { PrintableCheck } from '../../../../../models/entities/printable-check';
import { CsvExportService } from '../../../../../services/csv-export/csv-export.service';
import { PrintableCheckBatch } from '../../../../../models/entities/printable-check-batch';
import { GenericDialogResult } from '../../../shared/generic-mat-dialog/generic-dialog-result';
import { LobBankAccount } from '../../../../../models/entities/lob-bank-account';
import { Payable } from '../../../../../models/entities/payable';
import { PayablesService } from '../../../../../services/payables/payables.service';
import { PayableActionType, PaymentMethod } from '../../../../../enums/payables/payable-action.enum';
import moment from 'moment-timezone';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { CheckPrintingSettingsService } from '../../../../../services/payables/check-printing-settings.service';
import { LegfiDatePipe } from '../../../../../pipes/format/legfi-date.pipe';
import { LobBankAccountSelectorComponent } from '../../../accounting/lob-bank-account-selector/lob-bank-account-selector.component';
import { CheckPrintingPreviewComponent } from '../check-printing-preview/check-printing-preview.component';

export interface CheckPrintingDialogResult extends GenericDialogResult
{
    hasNewChecks: boolean;
    payableProcessed?: boolean;
}

export interface CheckPrintingDialogData
{
    checkIds?: number[];
    payable?: Payable;
}

/**
 * Multi-modal dialog for creating physical check batches via Lob API integration. Supports
 * three distinct workflows: new check creation, existing check reprinting, and payable-linked
 * check generation with automatic payment processing.
 *
 * The component maintains check batch state post-creation to enable PDF download without
 * recreating the batch. When processing payables, the check batch must be created first
 * due to Lob API requirements, then linked to the payable for payment tracking.
 */
@UntilDestroy()
@Component({
    selector: 'app-check-printing-dialog',
    templateUrl: './check-printing-dialog.component.html',
    styleUrls: ['./check-printing-dialog.component.scss'],
    animations: [
        trigger('slide', [
            transition(':enter', [
                style({opacity: 0, transform: 'translateY(20%)'}),
                animate('300ms ease', style({opacity: 1, transform: 'translateY(0)'})),
            ]),
        ]),
    ],
    standalone: true,
    imports: [
        CommonModule,
        CurrencyInputComponent,
        ReactiveFormsModule,
        SharedModule,
        CheckNumberInputComponent,
        LegfiDatePipe,
        LobBankAccountSelectorComponent,
        CheckFormatOptionsComponent,
        CheckPrintingPreviewComponent,
        VendorEditorDialogComponent,
        ConfirmDialogComponent,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CheckPrintingDialogComponent implements OnInit
{
    @Input() open = false;
    @Input() checkIds: number[] = [];
    @Input() payable: Payable | null = null;
    @Output() closed = new EventEmitter<CheckPrintingDialogResult | null>();

    addVendorOpen = false;
    duplicateMatches: string[] = [];
    duplicateConfirmOpen = false;
    private _duplicateAnswer: Subject<boolean> | null = null;
    checkBatch: PrintableCheckBatch;
    checkForm: UntypedFormGroup;
    currentCheckIndex: number = 0;
    formatForm: UntypedFormGroup;
    isLoading: boolean = false;
    jwt: JwtLegFiClaims;
    previewData: PrintableCheck[];
    vendors: Vendor[];
    lobBankAccounts: LobBankAccount[];
    verifiedBankAccounts: LobBankAccount[] = [];
    hasPayableToProcess: boolean = false;
    checkPrintingDefaults: any = {
        format: 'one-per-page',
        displayBankAccount: false,
        displayPayor: false,
        displayCheckNumber: false,
        displayVendorAddress: false
    };

    step: number = 0;
    private _formatOptions: CheckFormatOptionsComponent;

    constructor(
            private _checkPrintingService: CheckPrintingService,
            private _csvExportService: CsvExportService,
            private _formBuilder: UntypedFormBuilder,
            private _growlerService: GrowlerService,
            private _vendorService: VendorService,
            private _payablesService: PayablesService,
            private _bankAccountsService: BankAccountsService,
            private _checkPrintingSettingsService: CheckPrintingSettingsService,
    ) {
    }

    @ViewChild('formatOptions')
    set formatOptions(value: CheckFormatOptionsComponent) {
        this._formatOptions = value;
        if (value && this.checkPrintingDefaults && !value.formatForm.dirty) {
            value.setFormatValues(this.checkPrintingDefaults);
        }
    }
    get formatOptions(): CheckFormatOptionsComponent {
        return this._formatOptions;
    }

    get hasDetailsStep(): boolean {
        return !this.checkIds || this.checkIds.length === 0;
    }

    get stepLabels(): string[] {
        return this.hasDetailsStep ? ['Details', 'Format', 'Review'] : ['Format', 'Review'];
    }

    get currentStepLabel(): string {
        return this.stepLabels[this.step];
    }

    readonly payeeTypeItems = [
        { value: 'vendor', label: 'Vendor' },
        { value: 'other', label: 'Other' },
    ];

    get vendorItems(): { value: number; label: string }[] {
        const items = (this.vendors || []).map(vendor => ({
            value: vendor.id,
            label: vendor.nickname ? `${vendor.name} (${vendor.nickname})` : vendor.name,
        }));
        items.unshift({ value: -1, label: 'Add a vendor' });
        return items;
    }

    get checks(): UntypedFormArray {
        return this.checkForm.get('checks') as UntypedFormArray;
    }

    get currentCheck(): UntypedFormGroup {
        return this.checks.controls[this.currentCheckIndex] as UntypedFormGroup;
    }

    get createButtonText(): string {
        return this.hasPayableToProcess ? 'Create check batch & process payment' : 'Create check batch';
    }

    get isSubmitting(): boolean {
        return this.isLoading;
    }

    ngOnInit(): void {
        this.jwt = LegFiJwtService.read();
        this.initializeForm();
        this.hasPayableToProcess = !!this.payable;
        this.loadBankAccounts();
        this.loadCheckPrintingDefaults();
    }

    loadCheckPrintingDefaults(): void {
        this._checkPrintingSettingsService.getSettings()
                .pipe(take(1)) // Auto-unsubscribe after first emission
                .subscribe({
                    next: (settings) => {
                        this.checkPrintingDefaults = {
                            format: settings.defaultCheckFormat || 'one-per-page',
                            displayBankAccount: settings.defaultDisplayBankAccount || false,
                            displayPayor: settings.defaultDisplayPayor || false,
                            displayCheckNumber: settings.defaultDisplayCheckNumber || false,
                            displayVendorAddress: settings.defaultDisplayVendorAddress || false
                        };

                        if (this.formatOptions && !this.formatOptions.formatForm.dirty) {
                            this.formatOptions.setFormatValues(this.checkPrintingDefaults);
                        }
                    },
                });
    }

    loadBankAccounts(): void {
        this._bankAccountsService.getBillPayAccounts(this.jwt.orgId).subscribe({
            next: (accounts) => {
                this.lobBankAccounts = accounts;
                this.verifiedBankAccounts = accounts.filter(account => account.verified);

                if (this.verifiedBankAccounts.length === 1) {
                    if (this.payable) {
                        const check = this.checks.at(0) as UntypedFormGroup;
                        if (!check.get('lobBankAccountId').value) {
                            check.get('lobBankAccountId').setValue(this.verifiedBankAccounts[0].id);
                        }
                    } else {
                        setTimeout(() => {
                            if (this.currentCheck && !this.currentCheck.get('lobBankAccountId').value) {
                                this.currentCheck.get('lobBankAccountId').setValue(this.verifiedBankAccounts[0].id);
                            }
                        }, 0);
                    }
                }
            },
            error: () => {
                this._growlerService.oops('Failed to load bank accounts');
            }
        });
    }

    scrollToElement(): void {
        // check width of window
        if (window.innerWidth > 1024) {
            setTimeout(() => {
                const contentDiv = document.getElementById('dialogContent');
                contentDiv?.scrollTo({behavior: 'smooth', top: contentDiv.scrollHeight});
            }, 400);
        }
    }

    initializeForm(): void {
        if (this.checkIds?.length > 0) {
            this.isLoading = true;
            this._checkPrintingService.getChecksByIds(this.checkIds)
                    .pipe(untilDestroyed(this), finalize(() => this.isLoading = false))
                    .subscribe({
                        next: (checks) => {
                            this.previewData = checks.filter(check => !check.cancelledAt);
                            if (this.previewData.length === 0) {
                                this._growlerService.oops('No valid checks exist in the selection');
                                this.closed.emit({ success: false, hasNewChecks: false });
                            } else if (this.previewData.length !== this.checkIds.length) {
                                this._growlerService.warning('Warning', 'Some of the selected checks were voided or cancelled and may not be re-printed');
                            }
                        },
                        error: () => {
                            this._growlerService.oops('Error fetching check data');
                            this.closed.emit({ success: false, hasNewChecks: false });
                        },
                    });
        } else {
            this.getVendors();
            this.checkForm = this._formBuilder.group({
                checks: this._formBuilder.array([this.createCheckFormGroup()]),
            });

            if (this.payable) {
                this.prefillFormFromPayable(this.payable);
            }
        }

        this.formatForm = this._formBuilder.group({
            format: ['one-per-page'],
        });
    }

    createCheckFormGroup(): UntypedFormGroup {
        return this._formBuilder.group({
            payeeType: ['vendor'],
            payee: [''],
            lobBankAccountId: [''],
            amount: [
                '',
                [
                    Validators.required,
                    Validators.min(0.01),
                ],
            ],
            checkDate: [
                '',
                Validators.required,
            ],
            checkNumber: [''],
            vendorId: [
                '',
                Validators.required,
            ],
            memo: [''],
        });
    }

    /**
     * Locks vendor and amount fields when creating checks from approved payables to prevent
     * circumvention of approval workflows. Bank account remains editable for check routing.
     */
    prefillFormFromPayable(payable: Payable): void {
        const check = this.checks.at(0) as UntypedFormGroup;

        // First update values
        check.patchValue({
            payeeType: 'vendor',
            vendorId: payable.vendor.id,
            amount: payable.paymentAmount || payable.amount,
            payee: payable.vendor.name,
            memo: payable.memo || '',
            checkDate: moment().format('YYYY-MM-DD')
        });

        check.get('payeeType').disable();
        check.get('vendorId').disable();
        check.get('amount').disable();
        check.get('payee').disable();

        check.get('lobBankAccountId').setValidators([Validators.required]);
    }

    handleStepChange(newStep: number): void {
        this.step = newStep;

        if (newStep === 1 && this.formatOptions && this.checkPrintingDefaults && !this.formatOptions.formatForm.dirty) {
            this.formatOptions.setFormatValues(this.checkPrintingDefaults);
        }

        if (newStep === 2) {
            this.generatePreviewData();
        }
    }

    advanceFromDetails(): void {
        this.currentCheck.markAllAsTouched();
        if (this.currentCheck.valid) {
            this.handleStepChange(this.step + 1);
        }
    }

    addCheck(): void {
        if (this.currentCheck.valid) {
            const newCheckGroup = this.createCheckFormGroup();
            this.checks.push(newCheckGroup);
            this.currentCheckIndex = this.checks.length - 1;

            if (this.verifiedBankAccounts.length === 1) {
                newCheckGroup.get('lobBankAccountId').setValue(this.verifiedBankAccounts[0].id);
            }

            this.scrollToElement();
        } else {
            this.currentCheck.markAllAsTouched();
            this._growlerService.oops('Please complete the current check details before creating another.');
        }
    }

    editCheck(index: number): void {
        if (this.currentCheck.valid) {
            this.currentCheckIndex = index;
        } else {
            this.currentCheck.markAllAsTouched();
            this._growlerService.oops('Please complete the current check details before editing another.');
        }
    }

    deleteCheck(index: number): void {
        this.checks.removeAt(index);
        this.currentCheckIndex = this.checks.length - 1;
        this.scrollToElement();
    }

    handlePayeeTypeChange(value: string): void {
        const payeeControl = this.currentCheck.get('payee');
        const vendorControl = this.currentCheck.get('vendorId');
        if (value === 'vendor') {
            payeeControl.setValidators(null);
            payeeControl.setValue('');
            vendorControl.setValidators(Validators.required);
        } else {
            vendorControl.setValidators(null);
            vendorControl.setValue('');
            payeeControl.setValidators(Validators.required);
        }
    }

    getVendors(): void {
        this._vendorService.getOrgVendorList(this.jwt.orgId)
                .pipe(untilDestroyed(this))
                .subscribe((vendors) => {
                    this.vendors = vendors;
                });
    }

    getVendorName(check: AbstractControl): string {
        if (!check.get('vendorId').value) {
            return check.get('payee').value;
        }
        const vendor = this.vendors.find(v => v.id === check.get('vendorId').value);
        return vendor.name || check.get('payee').value;
    }

    checkAddVendor(vendorId: number): void {
        if (vendorId === -1) {
            this.addVendorOpen = true;
        } else {
            const vendor = this.vendors.find(v => v.id === vendorId);
            if (vendor) {
                this.currentCheck.get('payee').setValue(vendor.name);
            }
        }
    }

    onAddVendorClosed(result: VendorEditorDialogResult | null): void {
        this.addVendorOpen = false;
        if (result?.success) {
            this.vendors.push(result.vendor);
            this.currentCheck.patchValue({
                vendorId: result.vendor.id,
                payee: result.vendor.name,
            });
        } else {
            this.currentCheck.patchValue({
                vendorId: null,
            });
        }
    }

    generatePreviewData(): void {
        this.isLoading = true;
        this._checkPrintingService.getPrintingPreviewData(this.checkForm).pipe(
                untilDestroyed(this),
                finalize(() => this.isLoading = false),
        ).subscribe({
            next: (previewData) => {
                this.previewData = previewData;
            },
            error: () => {
                this._growlerService.oops('Error generating check preview data');
            },
        });
    }

    /**
     * Handles three submission paths: batch re-download, existing check reprinting, and new check
     * creation. For payable-linked checks, creates the batch first then updates payable status
     * to maintain transaction integrity even if the payable update fails.
     */
    submitForm(): void {
        // Prevent duplicate submissions from rapid clicks
        if (this.isLoading) {
            return;
        }

        this.isLoading = true;
        // existing batch
        if (this.checkBatch) {
            this.downloadPrintedCheckFile();
            return;
        }
        // existing check
        if (this.checkIds?.length > 0) {
            const formatValues = this.formatOptions?.getFormatValues() || this.checkPrintingDefaults;

            this._checkPrintingService
                    .submitExistingChecks(this.checkIds, formatValues)
                    .pipe(take(1), finalize(() => this.isLoading = false)).subscribe({
                next: (batch: PrintableCheckBatch) => {
                    this.checkBatch = batch;
                    this._growlerService.success('Success', 'Checks created successfully... click below to download the file');
                },
                error: (err) => {
                    console.error('Error submitting checks:', err);
                    this._growlerService.oops('Error submitting check data: ' + (err.error?.message || ''));
                },
            });
            return;
        }

        // new check batch
        if (!this._validateNewCheckForm()) {
            this.isLoading = false;
            this._growlerService.oops('Please complete all required fields before submitting.');
            return;
        }

        const checkData = this.checkForm.getRawValue();
        const formatValues = this.formatOptions?.getFormatValues() || this.checkPrintingDefaults;

        // SD-6308: warn if a near-duplicate check already exists for any vendor/amount/date
        // combination in the form. Most observed pattern-B duplicates were the user clicking
        // print a second time over many minutes — UI debounce won't catch that but a confirm
        // step will.
        this._confirmIfDuplicatesExist(checkData)
                .pipe(take(1))
                .subscribe((shouldProceed: boolean) => {
                    if (!shouldProceed) {
                        this.isLoading = false;
                        return;
                    }
                    this._submitNewCheckBatch(checkData, formatValues);
                });
    }

    private _validateNewCheckForm(): boolean {
        let isValid = true;
        this.checks.controls.forEach((control: UntypedFormGroup) => {
            if (control.get('payeeType').value === 'vendor' && !control.get('vendorId').value) {
                control.get('vendorId').markAsTouched();
                isValid = false;
            } else if (control.get('payeeType').value === 'other' && !control.get('payee').value) {
                control.get('payee').markAsTouched();
                isValid = false;
            }
            if (!control.get('checkDate').value) {
                control.get('checkDate').markAsTouched();
                isValid = false;
            }
            if (!control.get('amount').value) {
                control.get('amount').markAsTouched();
                isValid = false;
            }
        });
        return isValid;
    }

    /**
     * For each vendor-payee check in the form, look up active PrintableChecks in the org
     * with the same vendor / amount / ±14-day window of the proposed check_date. If any
     * are found, open a confirmation dialog listing them; the user can cancel or "Print
     * Anyway". Returns an Observable<boolean> indicating whether to proceed with submit.
     */
    private _confirmIfDuplicatesExist(checkData: any): Observable<boolean> {
        const checks: any[] = (checkData.checks || []).filter((c: any) =>
                c.payeeType === 'vendor' && c.vendorId && c.amount && c.checkDate
        );
        if (checks.length === 0) {
            return of(true);
        }

        const lookups: Observable<PrintableCheck[]>[] = checks.map((c: any) => {
            const amountCents = typeof c.amount === 'string'
                    ? parseInt(c.amount.replace(/[^0-9]/g, ''), 10)
                    : c.amount;
            const checkDate = moment.isMoment(c.checkDate)
                    ? c.checkDate.format('YYYY-MM-DD')
                    : moment(c.checkDate).format('YYYY-MM-DD');
            return this._checkPrintingService.findPotentialDuplicates(c.vendorId, amountCents, checkDate);
        });

        return forkJoin(lookups).pipe(
                switchMap((results: PrintableCheck[][]) => {
                    const matches: PrintableCheck[] = results.flat();
                    if (matches.length === 0) {
                        return of(true);
                    }
                    return this._openDuplicateConfirmation(matches);
                }),
                catchError((err) => {
                    // Don't block submission on a lookup failure — notify and proceed.
                    console.error('[CheckPrinting] potential-duplicates lookup failed:', err);
                    this._growlerService.warning('Warning', 'Could not verify potential duplicate checks — proceeding with submission.');
                    return of(true);
                }),
        );
    }

    private _openDuplicateConfirmation(matches: PrintableCheck[]): Observable<boolean> {
        this.duplicateMatches = matches.map((m: PrintableCheck) => {
            const vendorName = m.vendor?.name ?? 'this vendor';
            const dateStr = m.checkDate ? m.checkDate.format('MMM D, YYYY') : 'unknown date';
            const amountStr = ((Number(m.amount) || 0) / 100).toLocaleString('en-US', {style: 'currency', currency: 'USD'});
            const checkNum = m.checkNumber ? `#${m.checkNumber}` : 'no check number';
            return `${amountStr} to ${vendorName} on ${dateStr} (${checkNum})`;
        });
        this._duplicateAnswer = new Subject<boolean>();
        this.duplicateConfirmOpen = true;
        return this._duplicateAnswer.asObservable();
    }

    onDuplicateConfirmClosed(confirmed: boolean): void {
        this.duplicateConfirmOpen = false;
        const answer = this._duplicateAnswer;
        this._duplicateAnswer = null;
        answer?.next(confirmed);
        answer?.complete();
    }

    private _submitNewCheckBatch(checkData: any, formatValues: any): void {
        this._checkPrintingService
                .submitPrintedChecks(checkData, formatValues)
                .pipe(
                        finalize(() => this.isLoading = false),
                        switchMap((batch: PrintableCheckBatch) => {
                            this.checkBatch = batch;
                            // If there's a payable to process, update its state
                            if (this.hasPayableToProcess && this.payable?.id) {
                                const payableData = {
                                    payableAction: {
                                        invoiceAction: PayableActionType.SCHEDULE as const,
                                        paymentMethod: PaymentMethod.PRINT_CHECK,
                                        printableCheckBatchId: batch.id,
                                        // Use the date from the check since there should only be one with a payable
                                        paymentDate: moment(checkData.checkDate).format('YYYY-MM-DD'),
                                    },
                                    organizationId: this.jwt.orgId,
                                };

                                return this._payablesService.updatePayablePaymentMethodPostApproval(
                                        this.payable.id,
                                        payableData,
                                ).pipe(
                                        switchMap(() => of({batch, payableProcessed: true})),
                                        catchError(error => {
                                            console.error('[CheckPrinting] Error updating payable status:', error);
                                            // Allow flow to continue if payable fails update — notify the user it didn't update.
                                            this._growlerService.warning('Warning', 'Check batch created but payable status could not be updated');
                                            return of({batch, payableProcessed: false});
                                        }),
                                );
                            }
                            return of({batch, payableProcessed: false});
                        }),
                        take(1),
                )
                .subscribe({
                    next: (result) => {
                        let successMessage = 'Checks created successfully... click below to download the file';

                        if (this.hasPayableToProcess && result.payableProcessed) {
                            successMessage = 'Checks created and payment processed successfully... click below to download the file';
                        }

                        this._growlerService.success('Success', successMessage);
                    },
                    error: (err) => {
                        console.error('Error submitting checks:', err);
                        this._growlerService.oops('Error submitting check data: ' + (err.error?.message || ''));
                    },
                });
    }

    downloadPrintedCheckFile(): void {
        this.isLoading = true;
        this._checkPrintingService.downloadPrintedCheckFile(this.checkBatch.id).pipe(
                take(1),
                finalize(() => this.isLoading = false),
        ).subscribe({
            next: (response) => {
                this._csvExportService.downloadFile(response, 'check.pdf', 'application/pdf');
                this.closed.emit({
                    success: true,
                    hasNewChecks: this.checkIds?.length === 0 || this.checkBatch?.checks?.length > 0,
                    payableProcessed: this.hasPayableToProcess,
                });
            },
            error: () => {
                this._growlerService.oops('Error downloading check');
            },
        });
    }

    close(): void {
        this.closed.emit(null);
    }

    trackByIndex(index: number): number {
        return index;
    }
}
